import { IceCream2, Sparkles } from "lucide-react";
import { BRAND } from "../lib/brand";
import { usePageMeta } from "../hooks/usePageMeta";
import { PAGE_SEO } from "../lib/seo";

const STORY_PARAGRAPHS = [
  "At Just Dessert Me — Hamilton's family dessert destination — we've spent years perfecting what we believe a great dessert experience should be.",
  "Behind every item on our menu is countless hours of testing different recipes, ingredients, suppliers, and techniques to create desserts that are genuinely worth getting excited about. Along the way, we learned one simple truth: the best desserts in Hamilton are made fresh.",
  "That's why every waffle in Hamilton, pancake and rolled ice cream we serve is made to order right in front of you — never pre-made, frozen, or simply reheated. It's also why our famous Cookie Pies and Loaded Cookies are baked fresh in our Hillcrest commercial kitchen every single day.",
  "Our goal has always been to create more than just another dessert shop in Hamilton. We wanted to build a dessert cafe where incredible flavours, quality ingredients, and a unique customer experience come together to create something truly special.",
  "That vision is exactly what inspired our transformation into Just Dessert Me.",
  "Whether you're visiting us in-store to watch your dessert being created or enjoying our famous Cookie Pies and Loaded Cookies delivered anywhere in New Zealand, we're passionate about making every treat memorable.",
];

const About = () => {
  usePageMeta(PAGE_SEO.about);
  return (
    <main data-testid="page-about" className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
      {/* HERO */}
      <section className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 fade-up">
          <p className="text-xs uppercase tracking-[0.3em] text-[#00b4ff] mb-3">Our story</p>
          <h1 className="font-script text-6xl sm:text-7xl lg:text-[7rem] neon-gradient-text leading-[0.95]">
            Years in the<br />making.
          </h1>
          <p className="text-gray-300 mt-6 leading-relaxed text-lg max-w-xl">
            {STORY_PARAGRAPHS[0]}
          </p>
        </div>
        <div className="lg:col-span-5 fade-up delay-2">
          <div className="aspect-[4/5] rounded-[2.5rem] overflow-hidden neon-frame-blue">
            <img
              src={BRAND.storefront}
              alt={`${BRAND.name} storefront`}
              data-testid="about-storefront-image"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* STORY BODY */}
      <section className="mt-20 max-w-3xl mx-auto fade-up">
        <div data-testid="about-story-body" className="space-y-6 text-gray-300 leading-relaxed text-base sm:text-lg">
          {STORY_PARAGRAPHS.slice(1).map((p) => (
            <p key={p.slice(0, 32)}>{p}</p>
          ))}
          <p className="flex items-center gap-2 text-white text-lg sm:text-xl pt-2">
            <span>If you haven't experienced Just Dessert Me yet, there's never been a better time to start.</span>
            <IceCream2 className="w-5 h-5 text-[#ff1a8c]" />
            <Sparkles className="w-5 h-5 text-[#00b4ff]" />
          </p>
        </div>
      </section>

      <hr className="hr-neon my-24" />

      {/* VALUES */}
      <section className="grid md:grid-cols-3 gap-8">
        {[
          { t: "Made fresh, in front of you", d: "Pancakes, waffles and rolled ice cream in Hamilton — made to order, never pre-prepped, never reheated." },
          { t: "Baked daily in-house", d: "Our Cookie Pies Hamilton-loves and Loaded Cookies come out of our commercial kitchen in Hillcrest every single day." },
          { t: "Delivered NZ-wide", d: "Cookie Pies and Loaded Cookies ship anywhere in New Zealand — flat-rate dessert delivery from Hamilton on every order." },
        ].map((v, i) => (
          <div
            key={v.t}
            data-testid={`value-card-${i}`}
            className="glass rounded-3xl p-8 border border-white/10 hover:border-[#ff1a8c]/60 transition-all"
          >
            <span className="text-[0.7rem] uppercase tracking-[0.3em] text-[#ff1a8c]">Value 0{i + 1}</span>
            <h3 className="font-script text-3xl text-white mt-2">{v.t}</h3>
            <p className="text-sm text-gray-400 mt-3 leading-relaxed">{v.d}</p>
          </div>
        ))}
      </section>

      <section className="mt-24 text-center">
        <h2 className="font-script text-5xl text-white">Visit us tonight</h2>
        <p className="text-gray-400 mt-4">{BRAND.address} · {BRAND.hours}</p>
      </section>
    </main>
  );
};

export default About;
